export const FEATURE_KEYS = [
  'editor.basic', 'editor.advanced', 'booking.enabled', 'booking.artist_selection',
  'booking.location_selection', 'artists.enabled', 'artists.max_count',
  'locations.max_count', 'team.enabled', 'quiz.enabled', 'analytics.basic',
  'analytics.advanced', 'downloads.clean', 'web_widget.enabled',
  'lead_capture.enabled', 'lead_capture.clean_download_gate',
] as const;

export type FeatureKey = typeof FEATURE_KEYS[number];
export type PlanCode = 'founder' | 'solo' | 'studio' | 'custom';
export type SubscriptionStatus = 'active' | 'trial' | 'grace' | 'past_due' | 'suspended';
export type CapabilityVisibility = 'visible' | 'locked' | 'hidden';

export interface CapabilityValue {
  enabled: boolean;
  visibility: CapabilityVisibility;
  limit: number | null;
}

export type ResolvedCapabilities = Record<FeatureKey, CapabilityValue>;

const on = (limit: number | null = null): CapabilityValue => ({ enabled: true, visibility: 'visible', limit });
const locked = (limit: number | null = null): CapabilityValue => ({ enabled: false, visibility: 'locked', limit });
const hidden = (): CapabilityValue => ({ enabled: false, visibility: 'hidden', limit: null });

export const PLAN_DEFAULTS: Record<PlanCode, ResolvedCapabilities> = {
  founder: {
    'editor.basic': on(), 'editor.advanced': on(), 'booking.enabled': on(), 'booking.artist_selection': on(),
    'booking.location_selection': on(), 'artists.enabled': on(), 'artists.max_count': on(), 'locations.max_count': on(),
    'team.enabled': on(), 'quiz.enabled': on(), 'analytics.basic': on(), 'analytics.advanced': on(),
    'downloads.clean': on(), 'web_widget.enabled': on(), 'lead_capture.enabled': on(), 'lead_capture.clean_download_gate': on(),
  },
  solo: {
    'editor.basic': on(), 'editor.advanced': locked(), 'booking.enabled': on(), 'booking.artist_selection': locked(),
    'booking.location_selection': locked(1), 'artists.enabled': on(), 'artists.max_count': on(1), 'locations.max_count': on(1),
    'team.enabled': hidden(), 'quiz.enabled': on(), 'analytics.basic': on(), 'analytics.advanced': locked(),
    'downloads.clean': on(), 'web_widget.enabled': locked(), 'lead_capture.enabled': locked(), 'lead_capture.clean_download_gate': locked(),
  },
  studio: {
    'editor.basic': on(), 'editor.advanced': on(), 'booking.enabled': on(), 'booking.artist_selection': on(),
    'booking.location_selection': on(), 'artists.enabled': on(), 'artists.max_count': on(20), 'locations.max_count': on(5),
    'team.enabled': on(), 'quiz.enabled': on(), 'analytics.basic': on(), 'analytics.advanced': on(),
    'downloads.clean': on(), 'web_widget.enabled': on(), 'lead_capture.enabled': on(), 'lead_capture.clean_download_gate': on(),
  },
  custom: {
    'editor.basic': on(), 'editor.advanced': on(), 'booking.enabled': on(), 'booking.artist_selection': on(),
    'booking.location_selection': on(), 'artists.enabled': on(), 'artists.max_count': on(), 'locations.max_count': on(),
    'team.enabled': on(), 'quiz.enabled': on(), 'analytics.basic': on(), 'analytics.advanced': on(),
    'downloads.clean': on(), 'web_widget.enabled': on(), 'lead_capture.enabled': on(), 'lead_capture.clean_download_gate': on(),
  },
};

// These capabilities are reserved for the web-widget visitor funnel. They do
// not gate Telegram tenant funnel_leads, booking_requests, or their analytics.
export const WEB_WIDGET_LEAD_FEATURES = [
  'lead_capture.enabled',
  'lead_capture.clean_download_gate',
] as const satisfies readonly FeatureKey[];

export function capabilityValue(capabilities: ResolvedCapabilities | undefined, key: FeatureKey): CapabilityValue {
  return capabilities?.[key] || PLAN_DEFAULTS.founder[key];
}

export function capabilityIsEnabled(capabilities: ResolvedCapabilities | undefined, key: FeatureKey): boolean {
  const capability = capabilityValue(capabilities, key);
  return capability.enabled && capability.visibility === 'visible';
}

export function isFeatureKey(value: string): value is FeatureKey {
  return (FEATURE_KEYS as readonly string[]).includes(value);
}

export function isPlanCode(value: string): value is PlanCode {
  return ['founder', 'solo', 'studio', 'custom'].includes(value);
}

export function isSubscriptionStatus(value: string): value is SubscriptionStatus {
  return ['active', 'trial', 'grace', 'past_due', 'suspended'].includes(value);
}

export function subscriptionAllowsConsumerRuntime(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trial' || status === 'grace';
}
